import { useMemo } from "react"
import { VStack, Text, Input, Checkbox, Collapsible } from "@chakra-ui/react"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useCustomersForSelect } from "../../hooks/useCustomersForSelect"
import { useSitesForCustomer } from "../../hooks/useSitesForCustomer"
import { useSystemsForSite } from "../../hooks/useSystemsForSite"
import { useAssetAssignment } from "../../hooks/useAssetAssignment"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { isFormValid } from "../../utils/assignmentFormState"
import {
  AssignmentFormProvider,
  useAssignmentFormState,
  useAssignmentFormActions,
} from "../../providers/AssignmentFormProvider"

interface AssignAssetDialogProps {
  assetId: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

// --- Individual field components ---

const CustomerField = () => {
  const { customerId } = useAssignmentFormState()
  const { setCustomer } = useAssignmentFormActions()
  const { customers, loading } = useCustomersForSelect()

  const items = useMemo(
    () => [
      { value: "", label: loading ? "Loading..." : "Select customer..." },
      ...customers.map((c) => ({ value: c.id, label: c.name })),
    ],
    [customers, loading],
  )

  return (
    <Field label="Customer" required>
      <NativeSelectRoot disabled={loading}>
        <NativeSelectField
          value={customerId ?? ""}
          onChange={(e) => setCustomer(e.target.value || null)}
          items={items}
        />
      </NativeSelectRoot>
    </Field>
  )
}

const SiteField = () => {
  const { customerId, siteId } = useAssignmentFormState()
  const { setSite } = useAssignmentFormActions()
  const { sites, loading } = useSitesForCustomer(customerId)

  const items = useMemo(
    () => [
      { value: "", label: !customerId ? "Select customer first..." : loading ? "Loading..." : "Select site..." },
      ...sites.map((s) => ({ value: s.id, label: s.name })),
    ],
    [sites, loading, customerId],
  )

  return (
    <Field label="Site" required>
      <NativeSelectRoot disabled={!customerId || loading}>
        <NativeSelectField
          value={siteId ?? ""}
          onChange={(e) => setSite(e.target.value || null)}
          items={items}
        />
      </NativeSelectRoot>
    </Field>
  )
}

const SystemField = () => {
  const { siteId, systemId, createNewSystem, customerId } = useAssignmentFormState()
  const { setSystem } = useAssignmentFormActions()
  const { systems, loading } = useSystemsForSite(siteId, customerId)

  const hasNoSystems = siteId && !loading && systems.length === 0

  const items = useMemo(
    () => [
      {
        value: "",
        label: !siteId
          ? "Select site first..."
          : loading
          ? "Loading..."
          : hasNoSystems
          ? "No systems available"
          : "Select system...",
      },
      ...systems.map((s) => ({ value: s.id, label: s.name })),
    ],
    [systems, loading, siteId, hasNoSystems],
  )

  if (createNewSystem) return null

  return (
    <NativeSelectRoot disabled={!siteId || loading || !!hasNoSystems}>
      <NativeSelectField
        value={systemId ?? ""}
        onChange={(e) => setSystem(e.target.value || null)}
        items={items}
      />
    </NativeSelectRoot>
  )
}

const NewSystemSection = () => {
  const { siteId, createNewSystem, newSystemName } = useAssignmentFormState()
  const { setCreateNewSystem, setNewSystemName } = useAssignmentFormActions()

  if (!siteId) return null

  return (
    <>
      <Field>
        <Checkbox.Root
          checked={createNewSystem}
          onCheckedChange={(e) => setCreateNewSystem(!!e.checked)}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>Create a new system for this site</Checkbox.Label>
        </Checkbox.Root>
      </Field>

      <Collapsible.Root open={createNewSystem} width="100%">
        <Collapsible.Content width="100%">
          <VStack gap={4} width="100%">
            <Field label="System name" required={createNewSystem}>
              <Input
                value={newSystemName}
                onChange={(e) => setNewSystemName(e.target.value)}
                placeholder="Enter system name"
              />
            </Field>
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
    </>
  )
}

// --- Dialog footer ---

const AssignAssetDialogFooter = ({ assetId, onSuccess, onClose }: { assetId: string; onSuccess: () => void; onClose: () => void }) => {
  const state = useAssignmentFormState()
  const { reset } = useAssignmentFormActions()
  const { createAssignment, creating } = useAssetAssignment(assetId, () => {})

  const formValid = isFormValid(state)

  const handleSave = async () => {
    if (!formValid) return

    try {
      await createAssignment({
        assetId,
        customerId: state.customerId!,
        siteId: state.siteId!,
        systemId: state.createNewSystem ? undefined : (state.systemId ?? undefined),
        newSystemName: state.createNewSystem && state.newSystemName.trim() ? state.newSystemName.trim() : undefined,
      })
      reset()
      onSuccess()
    } catch (error: any) {
      toaster.error({
        title: "Failed to assign asset",
        description: error?.message || "An unexpected error occurred",
      })
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <>
      <ActionButton
        disabled={!formValid || creating}
        context={{ label: "Save", description: "Save the asset assignment", variant: "primary" }}
        onClick={handleSave}
      />
      <ActionButton
        disabled={creating}
        context={{ label: "Cancel", description: "Cancel the assignment", variant: "secondary" }}
        onClick={handleClose}
      />
    </>
  )
}

// --- Main dialog ---

const AssignAssetDialogInner = ({ assetId, open, onClose, onSuccess }: AssignAssetDialogProps) => {
  const { reset } = useAssignmentFormActions()

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog
      title="Assign asset"
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) handleClose()
      }}
      onClose={handleClose}
      footer={<AssignAssetDialogFooter assetId={assetId} onSuccess={onSuccess} onClose={onClose} />}
    >
      <VStack gap={4} width="100%" align="stretch">
        <CustomerField />
        <SiteField />
        <Field label="System" optionalText={<Text fontSize="xs" color="fg.muted">(optional)</Text>}>
          <SystemField />
        </Field>
        <NewSystemSection />
      </VStack>
    </Dialog>
  )
}

// --- Exported component ---

const AssignAssetDialog = (props: AssignAssetDialogProps) => {
  return (
    <AssignmentFormProvider>
      <AssignAssetDialogInner {...props} />
    </AssignmentFormProvider>
  )
}

export default AssignAssetDialog
